/**
 * C 트레이 배치와 챔버 표.
 * 출처: 와이어프레임 `TRAY`(1316행) · `SAMPLES`(1068행) · `chamberCls`/`chamberStage`(1070행).
 *
 * # 🔴 이 파일의 `slot` · `position` · `verdict` 는 **서버에 없다. demo 전용이다.**
 *
 * 2026-09-20 전수 확인(`docs/backend-requests.csv` BR-31). **fixture 가 실물보다 «부자»인 자리**라
 * 위험하다 — 다음 사람이 「응답에 있는 값」으로 착각한다. 실제로 그렇게 새어 나간 적이 있다:
 * `MonitoringScreen` 의 기본값이 이 파일이라, **모듈이 전부 `IDLE` 인 live 화면의 로터에
 * `S-1045`~`S-1048` 이 돌고 있었다.**
 *
 * | 왜 서버에 없나 | 근거 |
 * | --- | --- |
 * | 칸 배정 축이 **시스템 전체에** 없다 | `POST /batches` 본문이 `{command_id, recipe_id, sample_ids[]}` 셋뿐 — 「어느 시료가 어느 칸에」를 받는 자리가 없다 |
 * | `Sample.current_position` 이 **채워지지 않는다** | `module-core` v0.9 `_publish_stage` 가 `position` 을 발행하지 않는다. 실측: 도는 배치의 `GET /samples` **126 스냅샷 전수에 그 키가 0건**(2026-09-20) |
 * | 칸별 비전 판정이 없다 | Pre-flight 는 **배치 단위**다(BR-13) |
 *
 * ⚠ **백엔드가 덜 만든 것이 아니라 eric 결정이다** — 「트레이 단위로 움직이고 개별로 진행 상황을
 *   각각 체크하지는 않는다」(2026-09-14 · `ctcells-core` `record_batch_wide()` 주석).
 *   ⇒ 「요청하면 생기는 값」이 아니다. 여기 값을 늘려도 실물이 따라오지 않는다.
 *
 * 🔴 **live 컨테이너(`MonitoringContainer`)는 이 파일을 절대 쓰지 않는다.** 쓰는 것은
 *    `MonitoringDemo` 하나이고, 시험(`monitoringModel.test.ts`)이 그 경계를 지킨다.
 *
 * 🔴 **챔버 표는 시료 ID 기준 정렬이다**(README §14-4). 트레이 물리 배치 순서가 아니다.
 *    대조(Reference)는 환자 시료가 아니라 **맨 뒤**에 둔다.
 */
import type { ChamberRow, TrayCell } from '@ctcells/contract'

import { REFERENCE_SAMPLE_ID } from './samples.ts'

/**
 * 실물 C 트레이 3×3 배치. 행 순서 `[A1 A2 A3] [B1 B2 B3] [C1 C2 C3]` — 서버 웰 이름.
 * `C3` 은 대조 챔버 칸이다(와이어프레임 표기 `R`). 칸 이름은 `TRAY_SLOTS` 와 같아야 한다(시험이 지킨다).
 */
export const TRAY_LAYOUT: TrayCell[] = [
  { slot: 'A1', sample_id: 'S-1041', counterweight: false },
  { slot: 'A2', sample_id: 'S-1042', counterweight: false },
  { slot: 'A3', sample_id: 'S-1045', counterweight: false },
  { slot: 'B1', sample_id: 'S-1043', counterweight: false },
  { slot: 'B2', sample_id: 'S-1044', counterweight: false },
  { slot: 'B3', sample_id: 'S-1046', counterweight: false },
  { slot: 'C1', sample_id: 'S-1047', counterweight: false },
  { slot: 'C2', sample_id: 'S-1048', counterweight: false },
  { slot: 'C3', sample_id: REFERENCE_SAMPLE_ID, counterweight: true },
]

/** 비어 있는 트레이(적재 전). 「빈 상태」 경계 확인용 */
export const TRAY_LAYOUT_EMPTY: TrayCell[] = TRAY_LAYOUT.map((cell) => ({
  ...cell,
  sample_id: null,
}))

/** 챔버 표 정렬 규칙 — 시료 ID 오름차순, 대조는 맨 뒤 */
function bySampleId(a: ChamberRow, b: ChamberRow): number {
  if (a.reference !== b.reference) return a.reference ? 1 : -1
  return a.sample_id.localeCompare(b.sample_id, 'ko')
}

const SLOT_OF: Record<string, string> = Object.fromEntries(
  TRAY_LAYOUT.filter((c) => c.sample_id !== null).map((c) => [c.sample_id as string, c.slot]),
)

function row(
  sample_id: string,
  position: string,
  stage: ChamberRow['stage'],
  verdict: ChamberRow['verdict'],
): ChamberRow {
  return {
    sample_id,
    slot: SLOT_OF[sample_id] ?? '—',
    position,
    stage,
    /* 🔴 세 시나리오 모두 **도는 작업의 시료**다 — 끝났거나 폐기된 행은 이 표에 서지 않는다 */
    status: 'in_process',
    verdict,
    reference: sample_id === REFERENCE_SAMPLE_ID,
  }
}

/** 실행 중 — 회차 1(4칸)은 통과, 회차 2(4칸)는 진행 중, 대조는 회차 3 대기 */
export const CHAMBER_ROWS_RUNNING: ChamberRow[] = [
  row('S-1041', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1042', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1043', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1044', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1045', '원심분리기', 'registered', null),
  row('S-1046', '원심분리기', 'registered', null),
  row('S-1047', '원심분리기', 'registered', null),
  row('S-1048', '원심분리기', 'registered', null),
  row(REFERENCE_SAMPLE_ID, 'C 트레이', 'registered', null),
].sort(bySampleId)

/** 적재 대기 — 아직 아무 칸도 처리되지 않았다 */
export const CHAMBER_ROWS_READY: ChamberRow[] = TRAY_LAYOUT.map((cell) =>
  row(cell.sample_id as string, 'C 트레이', 'registered', null),
).sort(bySampleId)

/** 안전정지 — 회차 2 가 멈춰 있고 판정이 서지 않은 칸이 있다 */
export const CHAMBER_ROWS_ERROR: ChamberRow[] = [
  row('S-1041', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1042', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1043', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1044', 'A 트레이', 'centrifuged', 'pass'),
  row('S-1045', '원심분리기', 'registered', 'inconclusive'),
  row('S-1046', '원심분리기', 'registered', 'inconclusive'),
  row('S-1047', '원심분리기', 'registered', null),
  row('S-1048', '원심분리기', 'registered', null),
  row(REFERENCE_SAMPLE_ID, 'C 트레이', 'registered', null),
].sort(bySampleId)

/** 진행 작업이 없을 때 — 빈 표 */
export const CHAMBER_ROWS_EMPTY: ChamberRow[] = []
